import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Sidebar, Segment, Input, Icon } from "semantic-ui-react";
import {
  PageToolbar,
  PageToolbarButtonsPane,
} from "../../components/PageToolbar";
import { showMoreResources } from "../../actions/shared";
import LoadMoreSensor from "../../components/shared/LoadMoreSensor";
import { canLoadMore } from "../../app_shared_functions";
import { debounceInput } from "../../shared-functions/environment";
import { mapResourceNameToReduxSelector } from "../../selectors/resources";
import useHasCRUDAccess from "../../custom-hooks/useHasCRUDAccess";
import GridContext from "../../components/shared/grid-bits/context/GridContext";
import RegionsLoadingSpinner from "../../components/shared/RegionsLoadingSpinner";
import useSubscribe from "../../custom-hooks/useSubscribe";
import SortableProvisionedAppList from "./SortableProvisionedAppList";
import { mapResourceTypeToReduxList } from "../../components/shared/grid-bits/mapResourceTypeToReduxList";
import { valuesOf } from "../../shared-functions/objects";
import Breadcrumbs from "../../components/shared/breadcrumbs/Breadcrumbs";
import MultiActionButtons from "./bits/MultiActionsButtons";
import { findChild } from "../../components/sidebar/bits/helper";
import { appPages } from "../../shared-constants/app-pages";
import { push } from "connected-react-router";
import { toast } from "react-toastify";
import { KnowledgeBaseButton } from "../../components/knowledgebase/KnowledgeBaseButton";

const ProvisionedAppLists: React.FC = () => {
  const resourceType: keyof typeof mapResourceNameToReduxSelector =
    "marketplace_provisioned_apps";
  const resourceTypeOrchestration: keyof typeof mapResourceTypeToReduxList =
    "orchestration_stacks";
  const dispatch = useDispatch();
  useSubscribe(mapResourceTypeToReduxList[resourceType]);
  useSubscribe(mapResourceTypeToReduxList[resourceTypeOrchestration]);

  const hasCRUDAccess = useHasCRUDAccess("openstack");

  const {
    resourcesList: provisionedAppList,
    currentPage,
    zonesLeft,
    hiddenRegions,
  } = useSelector(mapResourceNameToReduxSelector[resourceType]);

  const breadcrumbs = useMemo(() => {
    return [
      {
        title: "marketplace.title",
        pathname: "/marketplace",
      },
      {
        title: "marketplace.provisioned.apps.title",
        popup: { Provisioned_Apps: valuesOf(provisionedAppList).length },
      },
    ];
  }, [provisionedAppList]);

  const [filteringText, setFilteringText] = useState("");

  const [loadingMore, setLoadingMore] = useState(false);

  const checkAndLoadMore = useCallback(
    (isVisible: boolean) => {
      if (
        isVisible &&
        canLoadMore(provisionedAppList, currentPage, hiddenRegions)
      ) {
        setLoadingMore(true);
        dispatch(showMoreResources("marketplace_provisioned_apps"));
      }
    },
    [hiddenRegions, currentPage, dispatch, provisionedAppList],
  );

  useEffect(() => {
    setLoadingMore(false);
  }, [currentPage]);

  const onFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      return debounceInput(() => setFilteringText(val))();
    },
    [],
  );

  const redirectToApps = useCallback(() => {
    const page = findChild(appPages, "Discover Apps");
    if (page?.link) {
      dispatch(push(page.link));
    } else {
      toast.error("Unable to redirect");
    }
  }, [dispatch]);

  const gridContextValues = useMemo(() => {
    return {
      filteringText,
      resourceType,
      hasCRUDAccess,
    };
  }, [resourceType, hasCRUDAccess, filteringText]);

  return (
    <div>
      <Sidebar.Pushable as={Segment}>
        <Sidebar.Pusher>
          <PageToolbar>
            <div className="flex align-items-center">
              <Breadcrumbs breadcrumbs={breadcrumbs} />
              <KnowledgeBaseButton />
            </div>
            <PageToolbarButtonsPane>
              <button
                className="button button--transparent"
                onClick={redirectToApps}
              >
                Provision new app
                <Icon name="plus circle" />
              </button>
            </PageToolbarButtonsPane>
          </PageToolbar>

          <div className="page-content" key="content">
            <div className="top-section">
              <MultiActionButtons />
              <div className="pos_right">
                <div className="ui input input-white">
                  <Input
                    minLength={2}
                    onChange={onFilterChange}
                    placeholder="Enter filter text..."
                  />
                </div>
              </div>
            </div>
            <GridContext.Provider value={gridContextValues}>
              <SortableProvisionedAppList />
            </GridContext.Provider>
            <LoadMoreSensor
              checkAndLoadMore={checkAndLoadMore}
              loading_more={loadingMore}
            />
          </div>

          <RegionsLoadingSpinner zonesLeft={zonesLeft} />
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </div>
  );
};

export default ProvisionedAppLists;
